import 'preact/devtools';
import React, { FC } from 'react';
import { Provider } from 'react-redux';
import { SWRConfig, SWRConfiguration } from 'swr';
import { SystemNotifications } from '../SystemNotifications';
import GeoLocation from '../GeoLocation';
import '../styles/global.scss';
import { ProfilesLocalStorageProvider } from '../ProfilesLocalStorageContext';
import 'regenerator-runtime/runtime';
import { FeatureFlagProvider } from '../contexts/FeatureFlagContext/FeatureFlagContext';
import { Store } from 'redux';
import ErrorBoundary from '../error/ErrorBoundary';
import { AriaLiveProvider } from '../contexts/AriaLiveContext/AriaLiveContext';
import { ServiceContextProvider } from '../contexts/ServiceContext/ServiceContext';
import {
  AppFeaturesContextProvider,
  Features,
} from '../contexts/AppFeaturesContext/AppFeaturesContext';
import { BrazeProvider } from '../braze/BrazeProvider';
import { InputMethodContextProvider } from '../contexts/InputMethodContext/InputMethodContext';
import { AnalyticsContextProvider } from '../contexts/AnalyticsContext/AnalyticsContextProvider';
import { ViewContextProvider } from '../contexts/ViewContext/ViewContext';
import { LoadingOverlay } from '../common/loading/LoadingOverlay';
import { isClient } from '../../common/utils';
import { AuthProvider } from '../contexts/AuthContext/AuthContext';
import { NotificationProvider } from '../contexts/NotificationContext/NotificationContext';
import { SidebarContextProvider } from '../contexts/SidebarContext/SidebarContext';
import Routes from '../Routes/Routes';
import { BrowserRouter } from 'react-router-dom';
import { Root } from 'react-dom/client';

const globalSWRConfig = {
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  shouldRetryOnError: false,
  onError: (error: unknown) => {
    throw error;
  },
} as const satisfies SWRConfiguration;

/* Avoid nag logging for useLayoutEffect, which has been removed in recent versions of React
 * https://gist.github.com/gaearon/e7d97cdf38a2907924ea12e4ebdf3c85
 */
if (typeof window === 'undefined') React.useLayoutEffect = React.useEffect;

export type AppProps = {
  store: Store<any, any>;
  features: Features;
  featureBrazeEnabled: boolean;
};

export const renderApp = (
  root: Root,
  store: Store<any, any>,
  features: Features,
  FEATURE_BRAZE: boolean
) => {
  root.render(
    <BrowserRouter>
      <App
        store={store}
        features={features}
        featureBrazeEnabled={FEATURE_BRAZE}
      />
    </BrowserRouter>
  );
};

export const App: FC<AppProps> = ({ store, features, featureBrazeEnabled }) => {
  if (!isClient()) return <LoadingOverlay isVisible={true} />;
  return (
    <AppFeaturesContextProvider {...features}>
      <ErrorBoundary>
        <SWRConfig
          value={{
            ...globalSWRConfig,
          }}
        >
          <FeatureFlagProvider brazeEnabled={featureBrazeEnabled}>
            <ProfilesLocalStorageProvider>
              <Provider store={store}>
                <AuthProvider>
                  <BrazeProvider language={features.languageCode}>
                    <NotificationProvider>
                      <GeoLocation>
                        <InputMethodContextProvider>
                          <ServiceContextProvider>
                            <AriaLiveProvider>
                              <AnalyticsContextProvider>
                                <ViewContextProvider>
                                  <SidebarContextProvider>
                                    <Routes />
                                  </SidebarContextProvider>
                                </ViewContextProvider>
                              </AnalyticsContextProvider>
                            </AriaLiveProvider>
                          </ServiceContextProvider>
                        </InputMethodContextProvider>
                        <SystemNotifications />
                      </GeoLocation>
                    </NotificationProvider>
                  </BrazeProvider>
                </AuthProvider>
              </Provider>
            </ProfilesLocalStorageProvider>
          </FeatureFlagProvider>
        </SWRConfig>
      </ErrorBoundary>
    </AppFeaturesContextProvider>
  );
};

export default App;
